import { AuthState } from 'core/context/reducer';
import { updateDebt, updateInvestment } from 'core/context';
import { BASE_URL, Data, IApplication } from 'core/utilities';
import { query, Result } from '.';

interface ApplicationResult extends Result 
{
    application? : IApplication;
}

export const getApplications = async (state : AuthState) : Promise<ApplicationResult> =>
{
    const { id, token } = state;
    if (!id || !token) 
    {
        return {
            success : false,
            error   : 'Invalid user id or token.'
        };
    }

    const options = {
        method : 'GET',
        headers : { 
            'Content-Type'  : 'application/json',
            'Authorization' : `Bearer ${token}`
        }
    };

    try 
    {
        const response = await fetch(`${BASE_URL}/api/v1/mortgage/`, options);
        const { results } = await response.json();

        return {
            success     : true,
            application : results.length > 0 ? results[0] : {}
        };
    }
    catch (error)
    {
        return {
            success : false,
            error   : error as string
        };
    }
};

interface CompleteApplicationResult extends Result 
{
    complete? : any;
}

export const getCompleteApplication = async (state : AuthState, applicationId : string) : Promise<CompleteApplicationResult> =>
{
    const { id, token } = state;
    if (!id || !token) 
    {
        return {
            success : false,
            error   : 'Invalid user id or token.'
        };
    }

    const options = {
        method : 'GET',
        headers : { 
            'Content-Type'  : 'application/json',
            'Authorization' : `Bearer ${token}`
        }
    };

    try 
    {
        const response = await fetch(`${BASE_URL}/api/v1/mortgage/application/complete/${applicationId}/`, options);
        const application = await response.json();

        return {
            success  : true,
            complete : application
        };
    }
    catch (error)
    {
        return {
            success : false,
            error   : error as string
        };
    }
};

export const updateEmploymentInfo = async (state : AuthState, payload : any) : Promise<Result> => 
{
    const { id, token } = state;
    if (!id || !token) 
    {
        return {
            success : false,
            error   : 'Invalid user id or token.'
        };
    }

    const {
        applicationId,
        employmentId,
        ...data
    } = payload;

    const options = {
        method : employmentId ? 'PATCH' : 'POST',
        headers : { 
            'Content-Type'  : 'application/json',
            'Authorization' : `Bearer ${token}`
        },
        body : JSON.stringify(data)
    };

    try 
    {
        const employment = employmentId ? `${employmentId}/` : '';
        await fetch(`${BASE_URL}/api/v1/mortgage/${applicationId}/applicant/employment/${employment}`, options);

        return {
            success : true
        };
    }
    catch (error)
    {
        return {
            success : false
        };
    }
};

interface ApplicantResult extends Result 
{
    applicant? : Data;
}

export const getApplicant = async (state : AuthState) : Promise<ApplicantResult> =>
{
    const { id, token } = state;
    if (!id || !token) 
    {
        return {
            success : false,
            error   : 'Invalid user id or token.'
        };
    }

    const { success, reply } = await query(
        'api/v1/user-applicant',
        { 'Authorization' : `Bearer ${token}` }
    );

    if (success)
    {
        const { applicant } = reply as { applicant : Data };
        return {
            success,
            applicant
        };
    }
    else 
    {
        return {
            success
        };
    }
};

export const updateApplicant = async (state : AuthState, payload : any) : Promise<Result> => 
{
    const { id, token } = state;
    if (!id || !token) 
    {
        return {
            success : false,
            error   : 'Invalid user id or token.'
        };
    }

    const {
        applicationId,
        applicantId,
        ...data
    } = payload;

    const options = {
        method : 'PATCH',
        headers : { 
            'Content-Type'  : 'application/json',
            'Authorization' : `Bearer ${token}`
        },
        body : JSON.stringify(data)
    };

    try 
    {
        await fetch(`${BASE_URL}/api/v1/mortgage/${applicationId}/applicant/${applicantId}/`, options);

        return {
            success : true
        };
    }
    catch (error)
    {
        return {
            success : false
        };
    }
};

export const addInvestmentProperty = async (state : AuthState, payload : any) : Promise<Result> => 
{
    const { id, token } = state;
    if (!id || !token) 
    {
        return {
            success : false,
            error   : 'Invalid user id or token.'
        };
    }

    const {
        applicationId,
        ...data
    } = payload;

    const options = {
        method : 'POST',
        headers : { 
            'Content-Type'  : 'application/json',
            'Authorization' : `Bearer ${token}`
        },
        body : JSON.stringify(data)
    };

    try 
    {
        const response = await fetch(`${BASE_URL}/api/v1/mortgage/${applicationId}/applicant/investment/property/`, options);
        const { error } = await response.json();

        if (error)
        {
            return {
                success : false,
                error
            };
        }

        return {
            success : true
        };
    }
    catch (error)
    {
        return {
            success : false,
            error : JSON.stringify(error)
        };
    }
};

export const updateInvestmentPropertyDetails = async (state : AuthState, payload : any) : Promise<Result> => 
{
    const { token } = state;
    const {
        applicationId,
        investment,
        property
    } = payload;

    const { id : investmentId } = investment;
    const { id : propertyId, ...data } = property;

    const options = {
        method : 'PATCH',
        headers : { 
            'Content-Type'  : 'application/json',
            'Authorization' : `Bearer ${token}`
        },
        body : JSON.stringify(data)
    };

    try
    {
        await fetch(`${BASE_URL}/api/v1/mortgage/${applicationId}/applicant/investment/${investmentId}/property/${propertyId}/`, options);
    
        return {
            success : true
        };
    }
    catch (error)
    {
        return {
            success : false,
            error   : JSON.stringify(error)
        };
    }
};

export const updateInvestmentPropertyUnified = async (state : AuthState, payload : any) : Promise<Result> => 
{
    const { id, token } = state;
    if (!id || !token) 
    {
        return {
            success : false,
            error   : 'Invalid user id or token.'
        };
    }

    try 
    {
        const updateInvestmentResult = await updateInvestment(state, payload);

        if (!updateInvestmentResult.success)
        {
            return updateInvestmentResult;
        }
        
        const updateDebtResult = await updateDebt(state, payload);

        if (!updateDebtResult.success)
        {
            return updateDebtResult;
        }

        const updateInvestmentPropertyDetailsResult = await updateInvestmentPropertyDetails(state, payload);

        if (!updateInvestmentPropertyDetailsResult)
        {
            return updateInvestmentPropertyDetailsResult;
        }

        return {
            success : true
        };
    }
    catch (error)
    {
        return {
            success : false,
            error : JSON.stringify(error)
        };
    }
};